import React, { useEffect, useState } from 'react'
import { Router } from '@reach/router'
import { compose } from 'recompose'
import { Toast } from 'src/components/Toast'
import ThemeProvider from 'src/theme/ThemeProvider'
import Layout from 'src/theme/Layout'
import PrivateRoute from 'src/components/PrivateRoute'
import initializeIntl from 'src/intl/initializeIntlWithMoment'
import routes from 'src/utils/routes'
import Dashboard from 'src/features/dashboard/Dashboard'
import NotificationsList from 'src/features/notifications/NotificationsList'
import { connectNotificationsBasics } from 'src/features/notifications/duck/connectors'
import Account from 'src/features/account/Account'
import MessagesList from 'src/features/messages/MessagesList'
import Conversation from 'src/features/messages/Conversation'
import { connectMessagesBasicData } from 'src/features/messages/duck/connectors'
import { connectUserAccount } from 'src/features/account/duck/connectors'
import { connectBootstrap } from 'src/ducks/connectors'
import PermissionsProvider from 'src/utils/permissions'
import TermsOfServiceDialog from 'src/features/termsOfService/TermsOfServiceDialog'
import BrowserBroadcastChannelService from 'src/services/BrowserBroadcastChannelService'
import NotificationCoordinatorService from 'src/services/NotificationCoordinatorService'
import ChatSyncService from 'src/services/ChatSyncService'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ensureSoundUnlocked } from 'src/features/messages/duck/actions'

const SessionQueryProvider = ({ children }) => {
  const [queryClient] = useState(() => new QueryClient())

  useEffect(() => () => queryClient.clear(), [queryClient])

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}

const AppPage = ({
  permissions,
  clearUserPermissions,
  clearConversation,
  bootstrapApplication,
  notificationsListOpened,
  listOpened,
  conversationOpened,
  currentUser
}) => {
  const intl = initializeIntl()

  useEffect(() => {
    bootstrapApplication()
    ensureSoundUnlocked()

    const broadcastService = BrowserBroadcastChannelService.getInstance()
    const notificationCoordinator = NotificationCoordinatorService.getInstance()
    const chatSyncService = ChatSyncService.getInstance()

    return () => {
      clearUserPermissions()
      clearConversation()
      broadcastService.destroy()
      notificationCoordinator.destroy()
      chatSyncService.destroy()
    }
  }, [])

  return (
    <SessionQueryProvider key={currentUser.id || 'anonymous'}>
      <ThemeProvider>
        <Toast />
        <PermissionsProvider.Provider value={permissions}>
          <Layout isUserLoggedIn>
            <Router basepath={`${intl.locale}`}>
              <PrivateRoute path={routes.APP_DASHBOARD} component={Dashboard} />
              <PrivateRoute path={routes.APP_ACCOUNT} component={Account} />
            </Router>
            {currentUser.id && <TermsOfServiceDialog />}
            {notificationsListOpened && <NotificationsList />}
            {listOpened && <MessagesList />}
            {conversationOpened && <Conversation />}
          </Layout>
        </PermissionsProvider.Provider>
      </ThemeProvider>
    </SessionQueryProvider>
  )
}

export default compose(
  connectUserAccount,
  connectBootstrap,
  connectNotificationsBasics,
  connectMessagesBasicData
)(AppPage)
